import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, AreaChart, Area, ComposedChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, Cell,
} from "recharts";
import { Lightbulb, TrendingUp, Users, Target, ArrowLeft, Database, FileSignature } from "lucide-react";
import { api } from "@/lib/api";
import { useStatusFilter } from "@/context/StatusFilterContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { PdfToolbar } from "@/components/PdfToolbar";
import { TrendBadge } from "@/components/TrendBadge";

const C = { bize: "#06B6D4", karsi: "#EF4444", field: "#10B981", work: "#F59E0B", users: "#8B5CF6", navy: "#0F2C59" };
const axis = { fontSize: 11, fill: "#94a3b8" };
const grid = "#1e293b";

function ChartCard({ title, children, sub, badge }) {
  return (
    <Card className="p-4 border border-border pdf-section">
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="font-display font-semibold text-sm">{title}</h3>
        {badge && <div className="flex items-center gap-1 shrink-0">{badge}</div>}
      </div>
      {sub && <p className="text-xs text-muted-foreground mb-2">{sub}</p>}
      <div className="h-64 w-full">{children}</div>
    </Card>
  );
}

export default function BigPicture() {
  const { status } = useStatusFilter();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("all");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/analytics/big-picture?status=${status}`).then(({ data }) => setD(data)).catch(() => setD(null)).finally(() => setLoading(false));
  }, [status]);

  const backfill = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backfill-vote-snapshots");
      toast.success(`Geçmiş dolduruldu: ${data.days} gün · ${data.records} kayıt`);
      const r = await api.get(`/analytics/big-picture?status=${status}`);
      setD(r.data);
    } catch {
      toast.error("Geçmiş doldurma başarısız oldu");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-64 rounded-lg" />)}</div>;
  if (!d) return <p className="text-muted-foreground">Veri yüklenemedi.</p>;

  const weeks = d.weeks || [];
  const shown = range === "all" ? weeks : weeks.slice(-parseInt(range, 10));
  const rf = shown[0] || {}, rl = shown[shown.length - 1] || {};
  const gBize = (rl.bize || 0) - (rf.bize || 0);
  const gKarsi = (rl.karsi || 0) - (rf.karsi || 0);
  const gNet = gBize - gKarsi;
  const fmtG = (n) => (n > 0 ? "+" : "") + n;
  const t = d.totals || {};

  return (
    <div className="space-y-4" data-testid="bigpicture-page" ref={ref}>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <button onClick={() => navigate("/panel")} className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-1" data-testid="bigpicture-back"><ArrowLeft className="h-3.5 w-3.5" /> Genel Panel</button>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2"><TrendingUp className="h-6 w-6 text-cyan-500" /> Büyük Resim</h1>
          <p className="text-sm text-muted-foreground">Analiz başlangıcı {d.start} · Günlük genel durum ({status === "faal" ? "Faal" : status === "aski" ? "Askı" : "Tümü"})</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "super_admin" && (
            <Button variant="outline" size="sm" onClick={backfill} disabled={busy} data-testid="bp-backfill-btn" className="border-[#0F2C59] text-[#0F2C59] dark:border-sky-400 dark:text-sky-300">
              <Database className="h-4 w-4 mr-1.5" /> {busy ? "Dolduruluyor..." : "Geçmişi Doldur"}
            </Button>
          )}
          <PdfToolbar targetRef={ref} filename="buyuk-resim.pdf" title="Büyük Resim — Seçim Analizi" />
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 pdf-section">
        {[
          { l: "Bizim Oy", v: t.bize, c: C.bize, i: Target },
          { l: "Karşı Oy", v: t.karsi, c: C.karsi, i: Target },
          { l: "Faal Firma", v: t.faal, c: C.navy, i: TrendingUp },
          { l: "Yetki Belgesi", v: `${t.belge ?? 0}/${t.belge_target ?? 0}`, c: "#7C3AED", i: FileSignature },
          { l: "Sisteme Katılan", v: t.users, c: C.users, i: Users },
        ].map((k) => (
          <Card key={k.l} className="p-4 border border-border" data-testid={`bp-kpi-${k.l}`}>
            <div className="flex items-center gap-2 text-muted-foreground text-xs"><k.i className="h-4 w-4" style={{ color: k.c }} /> {k.l}</div>
            <p className="text-2xl font-bold mt-1" style={{ color: k.c }}>{k.v ?? 0}</p>
          </Card>
        ))}
      </div>

      <div className="flex items-center gap-1.5 flex-wrap" data-testid="bp-range">
        <span className="text-xs text-muted-foreground mr-1">Gün aralığı:</span>
        {[{ v: "1", l: "Bugün" }, { v: "3", l: "Son 3 Gün" }, { v: "5", l: "Son 5 Gün" }, { v: "7", l: "Son 7 Gün" }, { v: "14", l: "Son 14 Gün" }, { v: "30", l: "Son 30 Gün" }, { v: "all", l: "Tümü" }].map((r) => (
          <button key={r.v} type="button" onClick={() => setRange(r.v)} data-testid={`bp-range-${r.v}`}
            className={`px-2.5 py-1 text-xs font-medium rounded-md border transition-colors ${range === r.v ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {r.l}
          </button>
        ))}
      </div>

      {shown.length >= 2 && (
        <div className="rounded-lg border border-border bg-muted/30 px-4 py-2.5 text-sm pdf-section" data-testid="bp-range-summary">
          <span className="text-muted-foreground">Seçili aralık ({rf.label} → {rl.label}) net kazanım:</span>{" "}
          <span className="font-semibold text-cyan-600" data-testid="bp-summary-bize">Bizim {fmtG(gBize)}</span>
          <span className="text-muted-foreground"> · </span>
          <span className="font-semibold text-red-500" data-testid="bp-summary-karsi">Karşı {fmtG(gKarsi)}</span>
          <span className="text-muted-foreground"> · </span>
          <span className={`font-semibold ${gNet >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid="bp-summary-net">Net {fmtG(gNet)}</span>
        </div>
      )}

      <ChartCard title="Bizim vs Karşı Oy — Günlük Seyir" sub="Gün gün toplam oy (çizgi grafik)"
        badge={<><TrendBadge data={shown} dataKey="bize" label="Bizim" /><TrendBadge data={shown} dataKey="karsi" label="Karşı" goodWhenUp={false} /><TrendBadge data={shown} dataKey="belge" label="Belge" /></>}>
        <ResponsiveContainer><LineChart data={shown}>
          <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis dataKey="label" tick={axis} /><YAxis tick={axis} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} /><Legend wrapperStyle={{ fontSize: 12 }} />
          <Line type="monotone" dataKey="bize" name="Bizim" stroke={C.bize} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} /><Line type="monotone" dataKey="karsi" name="Karşı" stroke={C.karsi} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} /><Line type="monotone" dataKey="belge" name="Yetki Belgesi" stroke="#7C3AED" strokeWidth={2} dot={{ r: 2 }} activeDot={{ r: 4 }} />
        </LineChart></ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <ChartCard title="Günlük Net Oy Artışı" sub="Her günkü değişim — bizim vs karşı">
          <ResponsiveContainer><BarChart data={shown}>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis dataKey="label" tick={axis} /><YAxis tick={axis} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="bize_delta" name="Bizim" fill={C.bize} radius={[4, 4, 0, 0]} /><Bar dataKey="karsi_delta" name="Karşı" fill={C.karsi} radius={[4, 4, 0, 0]} />
          </BarChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Kümülatif Oy" sub="Toplam bizim vs karşı (alan)"
          badge={<TrendBadge data={shown} dataKey="bize" label="Bizim" />}>
          <ResponsiveContainer><AreaChart data={shown}>
            <defs><linearGradient id="gb" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.bize} stopOpacity={0.6} /><stop offset="100%" stopColor={C.bize} stopOpacity={0.05} /></linearGradient><linearGradient id="gk" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.karsi} stopOpacity={0.6} /><stop offset="100%" stopColor={C.karsi} stopOpacity={0.05} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis dataKey="label" tick={axis} /><YAxis tick={axis} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="bize" name="Bizim" stroke={C.bize} fill="url(#gb)" /><Area type="monotone" dataKey="karsi" name="Karşı" stroke={C.karsi} fill="url(#gk)" />
          </AreaChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Saha Teması + Çalışma Skoru" sub="Günlük saha teması (alan) ve çalışma skoru (çizgi)"
          badge={<TrendBadge data={shown} dataKey="work_score" label="Skor" />}>
          <ResponsiveContainer><ComposedChart data={shown}>
            <defs><linearGradient id="gf" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.field} stopOpacity={0.5} /><stop offset="100%" stopColor={C.field} stopOpacity={0.05} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis dataKey="label" tick={axis} /><YAxis yAxisId="l" tick={axis} /><YAxis yAxisId="r" orientation="right" tick={axis} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Area yAxisId="l" type="monotone" dataKey="field" name="Saha Teması" stroke={C.field} fill="url(#gf)" /><Line yAxisId="r" type="monotone" dataKey="work_score" name="Çalışma Skoru %" stroke={C.work} strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart></ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Sisteme Katılan Kullanıcılar" sub="Günlük yeni (alan) ve toplam (çizgi)"
          badge={<TrendBadge data={shown} dataKey="users_cum" label="Toplam" />}>
          <ResponsiveContainer><ComposedChart data={shown}>
            <defs><linearGradient id="gu" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={C.users} stopOpacity={0.5} /><stop offset="100%" stopColor={C.users} stopOpacity={0.05} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis dataKey="label" tick={axis} /><YAxis tick={axis} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="users_new" name="Yeni" stroke={C.users} fill="url(#gu)" /><Line type="monotone" dataKey="users_cum" name="Toplam" stroke={C.navy} strokeWidth={2} dot={{ r: 2 }} />
          </ComposedChart></ResponsiveContainer>
        </ChartCard>
      </div>

      <ChartCard title="Komite Çalışma Performansı" sub="Çalışma skoruna göre ilk 12 komite — tıklayınca komite detayına gider">
        <ResponsiveContainer><BarChart data={(d.committees || []).slice(0, 12)} layout="vertical" margin={{ left: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={grid} /><XAxis type="number" tick={axis} domain={[0, 100]} /><YAxis type="category" dataKey="committee_no" tick={axis} width={30} /><Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} formatter={(v, n, p) => [`%${v}`, p.payload.name]} />
          <Bar dataKey="work_score" name="Çalışma Skoru" radius={[0, 4, 4, 0]} onClick={(e) => e && navigate(`/komite/${e.committee_no}`)} cursor="pointer">
            {(d.committees || []).slice(0, 12).map((c, i) => <Cell key={i} fill={c.karsi > c.bize ? C.karsi : C.bize} />)}
          </Bar>
        </BarChart></ResponsiveContainer>
      </ChartCard>

      <Card className="p-4 border border-amber-300 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20 pdf-section" data-testid="bp-insights">
        <h3 className="font-display font-semibold text-sm flex items-center gap-2 mb-2"><Lightbulb className="h-4 w-4 text-amber-500" /> Kritik Karar Noktaları / İçgörüler</h3>
        <ul className="space-y-1.5">
          {(d.insights || []).map((s, i) => <li key={i} className="text-sm text-foreground flex gap-2"><span className="text-amber-500">•</span>{s}</li>)}
        </ul>
      </Card>
    </div>
  );
}
